import { Link } from "react-router-dom";
import { Mail, Phone, ArrowUpRight } from "lucide-react";

const serviceCategories = [
  { name: "Design", path: "/services/design" },
  { name: "Web", path: "/services/web" },
  { name: "Social Marketing", path: "/services/social-marketing" },
  { name: "Digital Marketing", path: "/services/digital-marketing" },
  { name: "AI Services", path: "/services/ai-services" },
  { name: "Learn Skills", path: "/services/learn-skills" },
];

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const whatsappNumber = "441704777881";
  const whatsappLink = `https://wa.me/${whatsappNumber}`;

  return (
    <footer data-testid="footer" className="bg-[#0A0A0A] text-white border-t border-white/10">
      <div className="container-custom py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link 
              to="/" 
              data-testid="footer-logo"
              className="inline-block hover:opacity-90 transition-opacity"
            >
              <img src="/logo.png" alt="Barton" className="h-10 w-auto" />
            </Link>
            <p className="mt-4 text-white/60 text-sm leading-relaxed">
              Empowering small businesses with premium digital services. 
              Over 15 years of experience helping businesses grow online.
            </p>
          </div>

          {/* Services Column */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6">What We Do</h4>
            <ul className="space-y-3">
              {serviceCategories.map((service) => (
                <li key={service.path}>
                  <Link
                    to={service.path}
                    data-testid={`footer-service-${service.path.split('/').pop()}`}
                    className="text-white/60 hover:text-purple-400 transition-colors text-sm flex items-center gap-1 group"
                  >
                    {service.name}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6">Company</h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/"
                  data-testid="footer-home"
                  className="text-white/60 hover:text-purple-400 transition-colors text-sm"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/work"
                  data-testid="footer-work"
                  className="text-white/60 hover:text-purple-400 transition-colors text-sm"
                >
                  Our Work
                </Link>
              </li>
              <li>
                <Link
                  to="/blog"
                  data-testid="footer-blog"
                  className="text-white/60 hover:text-purple-400 transition-colors text-sm"
                >
                  Blog
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  data-testid="footer-contact"
                  className="text-white/60 hover:text-purple-400 transition-colors text-sm"
                >
                  Contact Us
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy-policy"
                  data-testid="footer-privacy"
                  className="text-white/60 hover:text-purple-400 transition-colors text-sm"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/website-terms"
                  data-testid="footer-website-terms"
                  className="text-white/60 hover:text-purple-400 transition-colors text-sm"
                >
                  Website Terms
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6">Get in Touch</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                <a 
                  href="mailto:hello@barton.uk" 
                  data-testid="footer-email"
                  className="text-white/60 hover:text-white transition-colors text-sm"
                >
                  hello@barton.uk
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-pink-400 mt-0.5 flex-shrink-0" />
                <div className="flex flex-col gap-1">
                  <a 
                    href="tel:01704777881" 
                    data-testid="footer-phone"
                    className="text-white/60 hover:text-white transition-colors text-sm"
                  >
                    01704 777881
                  </a>
                  <a 
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="footer-whatsapp"
                    className="text-[#25D366] hover:text-[#25D366]/80 transition-colors text-sm flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp
                  </a>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col gap-4">
          <p className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 text-sm text-center md:text-left font-medium">
            &copy; {currentYear} Barton. All rights reserved.
          </p>
          <p className="text-white/30 text-xs text-center md:text-left">
            Barton is a trading name of VZV Ltd, Registered in the United Kingdom & Wales No. 10689932
          </p>
        </div>
      </div>
    </footer>
  );
}
